import { useState, useMemo } from "react";
import { useHoldingsHistory } from "../hooks/useHoldingsHistory";
import { fmtMoney } from "../parsers/xmlParser";
import { cumulativeReturn, annualize } from "../utils/monthlyReturn";

function pct(r) {
  if (r == null || Number.isNaN(r)) return "—";
  return `${(r * 100).toFixed(2)}%`;
}
function pctColor(r) {
  if (r == null || Number.isNaN(r)) return "#94a3b8";
  return r >= 0 ? "#059669" : "#dc2626";
}
function periodLabel(p) {
  if (!p) return "";
  const [y, m] = p.split("-");
  return `${m}/${y}`;
}

// Label the insured-person deposit bucket for a snapshot: "עובד", "עצמאי", or
// "עובד/עצמאי" when funds disagree. (depositsBreakdown is absent on pre-upgrade snapshots.)
function insuredLabelOf(s) {
  const labels = new Set(
    (s.perFund || [])
      .filter((pf) => (pf.depositsBreakdown?.insured || 0) > 0)
      .map((pf) => pf.depositsBreakdown?.insuredLabel || "עובד")
  );
  if (labels.size === 1) return [...labels][0];
  if (labels.size > 1) return "עובד/עצמאי";
  return "עובד";
}

// Tooltip text breaking the month's deposits into employer / insured / one-time.
function depTitle(s) {
  const parts = [];
  if (s.totalEmployer) parts.push(`מעסיק: ${fmtMoney(s.totalEmployer)}`);
  if (s.totalInsured) parts.push(`${insuredLabelOf(s)}: ${fmtMoney(s.totalInsured)}`);
  if (s.totalOneTime) parts.push(`חד-פעמי / ניוד: ${fmtMoney(s.totalOneTime)}`);
  return parts.join(" · ");
}

const TH = { padding: "8px 10px", fontSize: 11, fontWeight: 700, color: "#64748b", textAlign: "right", whiteSpace: "nowrap" };
const TD = { padding: "8px 10px", fontSize: 12, color: "#334155", textAlign: "right", whiteSpace: "nowrap" };

function Sparkline({ values }) {
  const pts = values.filter((v) => v != null);
  if (pts.length < 2) return null;
  const w = 220, h = 44, pad = 4;
  const min = Math.min(...pts, 0), max = Math.max(...pts, 0);
  const range = max - min || 1;
  const step = (w - pad * 2) / (pts.length - 1);
  const x = (i) => pad + i * step;
  const y = (v) => pad + (h - pad * 2) * (1 - (v - min) / range);
  const d = pts.map((v, i) => `${i === 0 ? "M" : "L"}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(" ");
  const zeroY = y(0);
  return (
    <svg width={w} height={h} style={{ display: "block" }}>
      <line x1={pad} y1={zeroY} x2={w - pad} y2={zeroY} stroke="#e2e8f0" strokeDasharray="3 3" />
      <path d={d} fill="none" stroke="#4338ca" strokeWidth="2" />
      {pts.map((v, i) => (
        <circle key={i} cx={x(i)} cy={y(v)} r="2.5" fill={v >= 0 ? "#059669" : "#dc2626"} />
      ))}
    </svg>
  );
}

export default function MonthlyReturnPanel({ clientId, clientName, extraSnapshots, onClose }) {
  const hookSnaps = useHoldingsHistory(clientId); // ascending by period (008)
  // 008 history takes precedence; otherwise use the פרט snapshots (הכשרה/הראל/כלל).
  const snapshots = hookSnaps.length ? hookSnaps : (extraSnapshots || []);
  const isApprox = !hookSnaps.length && snapshots[0]?._approxReturn;
  const [tab, setTab] = useState("monthly"); // 'monthly' | 'timetravel'
  const [period, setPeriod] = useState(null);

  const hasData = snapshots.length > 0;
  const withReturn = snapshots.filter((s) => s.ret != null);
  const cum = useMemo(() => cumulativeReturn(snapshots.map((s) => s.ret)), [snapshots]);
  const ann = useMemo(() => annualize(cum, withReturn.length), [cum, withReturn.length]);
  const latest = snapshots[snapshots.length - 1] || null;

  const selected = useMemo(() => {
    const p = period || latest?.period;
    return snapshots.find((s) => s.period === p) || latest;
  }, [snapshots, period, latest]);

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 4200,
        background: "rgba(15,23,42,0.5)",
        display: "flex", alignItems: "center", justifyContent: "center",
        direction: "rtl",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "white", borderRadius: 16, width: 720, maxWidth: "94vw",
          maxHeight: "90vh", display: "flex", flexDirection: "column",
          boxShadow: "0 20px 60px rgba(0,0,0,0.25)", overflow: "hidden",
        }}
      >
        {/* Header */}
        <div style={{ background: "linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%)", color: "white", padding: "14px 18px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <button onClick={onClose} style={{ background: "transparent", border: "none", color: "white", fontSize: 18, cursor: "pointer" }}>✕</button>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontWeight: 800, fontSize: 17 }}>📈 תשואה חודשית — {clientName || "לקוח"}</div>
            <div style={{ fontSize: 11, opacity: 0.85 }}>{isApprox ? "תשואה גולמית משוערת (ללא נתוני הפקדות)" : "נטו מהפקדות (Modified Dietz)"} · {snapshots.length} חודשים</div>
          </div>
          <div style={{ width: 18 }} />
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", gap: 6, padding: "10px 16px 0" }}>
          {[
            { k: "monthly", label: "תשואה חודשית" },
            { k: "timetravel", label: "מבט אחורה" },
          ].map(({ k, label }) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              style={{
                padding: "7px 14px", fontSize: 13, fontWeight: 700,
                border: "none", borderBottom: `3px solid ${tab === k ? "#4338ca" : "transparent"}`,
                background: "transparent", color: tab === k ? "#4338ca" : "#64748b", cursor: "pointer",
              }}
            >
              {label}
            </button>
          ))}
        </div>
        <div style={{ height: 1, background: "#e2e8f0" }} />

        <div style={{ padding: 18, overflowY: "auto" }}>
          {!hasData && (
            <div style={{ textAlign: "center", color: "#94a3b8", padding: 40 }}>
              אין עדיין היסטוריית אחזקות ללקוח זה.
            </div>
          )}

          {hasData && tab === "monthly" && (
            <>
              {/* KPIs */}
              <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
                <Kpi label="חודש אחרון" value={pct(latest?.ret)} color={pctColor(latest?.ret)} sub={periodLabel(latest?.period)} />
                <Kpi label="מצטבר" value={pct(cum)} color={pctColor(cum)} sub={`${withReturn.length} חודשים`} />
                <Kpi label="משוער שנתי" value={pct(ann)} color={pctColor(ann)} />
                <Kpi label="שווי תיק" value={fmtMoney(latest?.totalBalance || 0)} color="#1e293b" />
              </div>

              <div style={{ marginBottom: 16 }}>
                <Sparkline values={snapshots.map((s) => s.ret)} />
              </div>

              {/* Monthly table */}
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ borderBottom: "1px solid #e2e8f0" }}>
                    {["חודש", "שווי", "הפקדות", "משיכות/ניוד", "תשואה"].map((h) => <th key={h} style={TH}>{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {[...snapshots].reverse().map((s) => (
                    <tr key={s.period} style={{ borderBottom: "1px solid #f1f5f9" }}>
                      <td style={{ ...TD, fontWeight: 700 }}>{periodLabel(s.period)}</td>
                      <td style={TD}>{fmtMoney(s.totalBalance)}</td>
                      <td style={TD} title={depTitle(s)}>
                        {s.totalDeposits ? fmtMoney(s.totalDeposits) : "—"}
                        {s.totalOneTime ? (
                          <span style={{ marginRight: 4, fontSize: 9, fontWeight: 700, color: "#d97706", background: "#fef3c7", borderRadius: 4, padding: "1px 4px" }}>חד״פ</span>
                        ) : null}
                      </td>
                      <td style={TD}>{s.totalWithdrawals ? fmtMoney(s.totalWithdrawals) : "—"}</td>
                      <td style={{ ...TD, fontWeight: 800, color: pctColor(s.ret) }}>{pct(s.ret)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          {hasData && tab === "timetravel" && selected && (
            <>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
                <label style={{ fontSize: 13, fontWeight: 700, color: "#475569" }}>בחר חודש:</label>
                <select
                  value={selected.period}
                  onChange={(e) => setPeriod(e.target.value)}
                  style={{ padding: "6px 10px", borderRadius: 7, border: "1px solid #cbd5e1", fontSize: 13 }}
                >
                  {[...snapshots].reverse().map((s) => (
                    <option key={s.period} value={s.period}>{periodLabel(s.period)}</option>
                  ))}
                </select>
              </div>

              {/* Portfolio summary for the chosen month */}
              <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
                <Kpi label="שווי תיק" value={fmtMoney(selected.totalBalance)} color="#1e293b" />
                <Kpi label="הפקדות בחודש" value={fmtMoney(selected.totalDeposits || 0)} color="#1e293b" />
                <Kpi label="משיכות/ניוד" value={fmtMoney(selected.totalWithdrawals || 0)} color="#1e293b" />
                <Kpi label="תשואת התיק" value={pct(selected.ret)} color={pctColor(selected.ret)} />
              </div>

              {/* Deposit breakdown for the chosen month */}
              {(selected.totalEmployer || selected.totalInsured || selected.totalOneTime) ? (
                <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: "#475569" }}>פירוט הפקדות:</span>
                  {selected.totalEmployer ? <Badge label="מעסיק" value={fmtMoney(selected.totalEmployer)} color="#2563eb" /> : null}
                  {selected.totalInsured ? <Badge label={insuredLabelOf(selected)} value={fmtMoney(selected.totalInsured)} color="#0891b2" /> : null}
                  {selected.totalOneTime ? <Badge label="חד-פעמי / ניוד" value={fmtMoney(selected.totalOneTime)} color="#d97706" /> : null}
                </div>
              ) : null}

              {/* Per-fund table */}
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ borderBottom: "1px solid #e2e8f0" }}>
                    {["קרן", "שווי", "הפקדות", "משיכות", "דמי ניהול", "תשואה"].map((h) => <th key={h} style={TH}>{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {selected.perFund.map((pf) => (
                    <tr key={pf.key} style={{ borderBottom: "1px solid #f1f5f9" }}>
                      <td style={{ ...TD, fontWeight: 700 }} title={pf.maslulim?.map((m) => m.name).join(", ")}>
                        {pf.companyName}{pf.polNum ? ` · ${pf.polNum}` : ""}
                      </td>
                      <td style={TD}>{fmtMoney(pf.balance)}</td>
                      <td style={TD} title={pf.depositsBreakdown ? depTitle({ totalEmployer: pf.depositsBreakdown.employer, totalInsured: pf.depositsBreakdown.insured, totalOneTime: pf.depositsBreakdown.oneTime, perFund: [pf] }) : ""}>
                        {pf.deposits ? fmtMoney(pf.deposits) : "—"}
                        {pf.depositsBreakdown?.oneTime ? (
                          <span style={{ marginRight: 4, fontSize: 9, fontWeight: 700, color: "#d97706", background: "#fef3c7", borderRadius: 4, padding: "1px 4px" }}>חד״פ</span>
                        ) : null}
                      </td>
                      <td style={TD}>{pf.withdrawals ? fmtMoney(pf.withdrawals) : "—"}</td>
                      <td style={TD}>{pf.dmeiNihulTzv != null ? `${pf.dmeiNihulTzv}% / ` : ""}{pf.dmeiNihulHaf != null ? `${pf.dmeiNihulHaf}%` : (pf.dmeiNihulTzv == null ? "—" : "")}</td>
                      <td style={{ ...TD, fontWeight: 800, color: pctColor(pf.ret) }}>{pct(pf.ret)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Badge({ label, value, color }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "#f8fafc", border: `1px solid ${color}33`, borderRadius: 999, padding: "4px 10px" }}>
      <span style={{ width: 8, height: 8, borderRadius: "50%", background: color }} />
      <span style={{ fontSize: 11, color: "#64748b" }}>{label}</span>
      <span style={{ fontSize: 12, fontWeight: 700, color: "#1e293b" }}>{value}</span>
    </span>
  );
}

function Kpi({ label, value, color, sub }) {
  return (
    <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10, padding: "10px 14px", minWidth: 120 }}>
      <div style={{ fontSize: 11, color: "#64748b", marginBottom: 3 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 800, color }}>{value}</div>
      {sub && <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 2 }}>{sub}</div>}
    </div>
  );
}
